// Email validation regex pattern
const EMAIL_VALIDATION_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/**
 * Generic function to perform fetch requests with enhanced error handling.
 * @param {object} config - Object containing endPoint, method, headers, body, and other fetch options
 * @param {string} config.endPoint - The API endpoint URL
 * @param {string} config.method - HTTP method (GET, POST, etc.)
 * @param {object} config.headers - Request headers
 * @param {string} config.body - Request body
 * @returns {Promise<Response>} - The fetch response promise
 * @throws {Error} - Throws error if request fails
 */
async function fetchData({ endPoint, method = 'GET', headers = {}, body, ...otherOptions }) {
  if (!endPoint) {
    throw new Error('Endpoint is required for fetch request');
  }

  try {
    const response = await fetch(endPoint, {
      method,
      headers,
      body,
      ...otherOptions,
    });

    return response;
  } catch (error) {
    throw new Error(`Fetch while processing the endpoint ${endPoint} error: ${error.message}`);
  }
}

/**
 * Validates an email address using a regular expression.
 * @param {string} email - The email address to validate
 * @returns {boolean} - Returns true if the email is valid, false otherwise
 */
function isEmailValid(email) {
  if (!email || typeof email !== 'string') {
    return false;
  }

  return EMAIL_VALIDATION_REGEX.test(email.trim());
}

/**
 * Displays a toast message with enhanced error handling.
 * @param {string} message - The message to display in the toast
 * @param {number} duration - Duration to show toast in milliseconds (default: 5000)
 */
function showToastMessage(message, duration = 5000) {
  const toast = document.getElementById('errorMessage');
  const messageElement = toast.querySelector('.error-message');

  messageElement.textContent = message;
  toast.classList.remove('hidden', 'opacity-0');
  toast.style.cssText = 'position: fixed; z-index: 9999';

  // Auto-hide toast after specified duration
  const hideTimeout = setTimeout(() => {
    hideToast(toast);
  }, duration);

  // Setup close button listener
  const closeButton = toast.querySelector('button[data-dismiss-target]');

  if (closeButton) {
    const handleCloseClick = function () {
      clearTimeout(hideTimeout);

      hideToast(toast);

      closeButton.removeEventListener('click', handleCloseClick);
    };
    closeButton.addEventListener('click', handleCloseClick);
  }
}

/**
 * Hide toast message utility function
 * @param {HTMLElement} toast - The toast element to hide
 */
function hideToast(toast) {
  if (toast) {
    toast.classList.add('hidden');
    toast.style.cssText = '';
  }
}

/**
 * Sets a value in session storage with error handling.
 * @param {string} key - The key under which to store the value.
 * @param {*} value - The value to store.
 * @returns {boolean} - Returns true if successful, false otherwise.
 */
function setSessionStorageItem(key, value) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return false;
    }

    // Serialize value to JSON string
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(key, serializedValue);
    return true;
  } catch (error) {
    console.error('Error setting session storage item:', error);
    return false;
  }
}

/**
 * Retrieves a value from session storage with proper error handling.
 * @param {string} key - The key of the item to retrieve.
 * @param {*} defaultValue - Default value to return if key doesn't exist or parsing fails.
 * @returns {*} - The retrieved value, defaultValue, or null if not found.
 */
function getSessionStorageItem(key, defaultValue = null) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return defaultValue;
    }

    const item = sessionStorage.getItem(key);

    // If the item doesn't exist, return the default value
    if (item === null) {
      return defaultValue;
    }

    return JSON.parse(item);
  } catch (error) {
    console.error('Error retrieving session storage item:', error);
    return defaultValue;
  }
}

/**
 * Deletes an item from session storage with error handling.
 * @param {string} key - The key of the item to delete.
 * @returns {boolean} - Returns true if successful, false otherwise.
 */
function deleteSessionStorageItem(key) {
  try {
    if (!key || typeof key !== 'string') {
      console.error('Invalid key provided for session storage');
      return false;
    }

    // Remove item from session storage
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error deleting session storage item:', error);
    return false;
  }
}

/**
 * Sets a value in local storage.
 * @param {string} key - The key under which to store the value.
 * @param {*} value - The value to store.
 */
function setLocalStorageItem(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

/**
 * Retrieves a value from local storage.
 * @param {string} key - The key of the item to retrieve.
 * @returns {*} - The retrieved value, or null if not found.
 */
function getLocalStorageItem(key) {
  return JSON.parse(localStorage.getItem(key));
}

/**
 * Deletes an item from local storage.
 * @param {string} key - The key of the item to delete.
 */
function deleteLocalStorageItem(key) {
  localStorage.removeItem(key);
}

/**
 * Toggles the visibility of a full-screen preloader with enhanced control.
 * @param {boolean} visible - Whether to show or hide the preloader
 * @param {object} options - Additional options for preloader
 */
function toggleFullScreenPreloader(visible, options = {}) {
  const { loaderId = 'loader', className = 'preloader', hideDelay = 500 } = options;

  let preloader = document.getElementById(loaderId);

  // Show or hide the preloader based on the 'visible' parameter
  if (visible) {
    if (!preloader) {
      preloader = createPreloaderElement(loaderId, className);
      document.body.appendChild(preloader);
    } else {
      preloader.classList.remove('preloader-invisible');
    }

    // Prevent body scrolling
    document.documentElement.style.overflowY = 'hidden';
  } else {
    if (preloader) {
      preloader.classList.add('preloader-invisible');
      setTimeout(() => {
        if (preloader && preloader.parentNode) {
          preloader.remove();
        }
      }, hideDelay);
    }

    // Restore body scrolling
    document.documentElement.style.overflowY = '';
  }
}

/**
 * Create preloader element
 * @param {string} id - Element ID
 * @param {string} className - CSS class name
 * @returns {HTMLElement} - Created preloader element
 */
function createPreloaderElement(id, className) {
  const preloader = document.createElement('div');
  preloader.classList.add(className);
  preloader.id = id;
  preloader.innerHTML = '<div class="preloader-circle"></div>';
  return preloader;
}

/**
 * Authentication Flow Control Utilities
 */

/**
 * Set the current authentication flow state
 * @param {string} state - The flow state to set
 */
function setAuthFlowState(state) {
  setSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE, state);
}

/**
 * Get the current authentication flow state
 * @returns {string} - The current flow state
 */
function getAuthFlowState() {
  return getSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE, AUTH_FLOW_STATES.INITIAL);
}

/**
 * Clear the authentication flow state
 */
function clearAuthFlowState() {
  deleteSessionStorageItem(SESSION_KEY.AUTH_FLOW_STATE);
}

/**
 * Check if user can access the verify email screen
 * @returns {boolean} - True if user can access verify email screen
 */
function canAccessVerifyEmail() {
  const flowState = getAuthFlowState();
  const profile = getSessionStorageItem(SESSION_KEY.PROFILE);

  // User can access verify email if they have submitted an email and have profile data
  return flowState === AUTH_FLOW_STATES.EMAIL_SUBMITTED && profile && profile.email;
}

/**
 * Check if user can access the register screen
 * @returns {boolean} - True if user can access register screen
 */
function canAccessRegister() {
  const flowState = getAuthFlowState();
  const profile = getSessionStorageItem(SESSION_KEY.PROFILE);

  // User can access register if they have verified their email and don't have a multipass token
  return flowState === AUTH_FLOW_STATES.REGISTRATION_ALLOWED && profile && profile.email && !profile.multipassToken;
}

/**
 * Redirect user to login page with proper cleanup
 */
function redirectToLogin() {
  clearAuthFlowState();
  deleteSessionStorageItem(SESSION_KEY.PROFILE);
  window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
}

/**
 * Utility method to set button loading state
 * @param {HTMLElement} button - The button element
 * @param {boolean} isLoading - Whether to set loading state or not
 */
function setButtonLoading(button, isLoading) {
  if (!button) return;

  button.disabled = isLoading;
  if (isLoading) {
    button.classList.add('loading');
  } else {
    button.classList.remove('loading');
  }
}

/** * Handles checkout button clicks to initiate the checkout process.
 * Sets a session storage item and redirects to the Shopify login page.
 */
function handleCheckout() {
  const checkoutElements = document.querySelectorAll('button[data-checkout]');

  checkoutElements.forEach(function (checkoutElement) {
    checkoutElement.addEventListener('click', function (event) {
      event.preventDefault();

      // Set session storage items for checkout process and return URL
      setSessionStorageItem(SESSION_KEY.CHECKOUT, true);
      setSessionStorageItem(SESSION_KEY.RETURN_TO, PAGE_URLS.CART);

      // Redirect to Shopify login page for authentication
      window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
    });
  });
}

/**
 * Initializes the accordion functionality.
 * Toggles the `aria-expanded` attribute of buttons to expand/collapse items.
 * @param {string} selector - The CSS selector for the accordion buttons.
 */
function initializeAccordion(selector) {
  const accordionItems = document.querySelectorAll(selector);

  // Add click event listeners to toggle aria-expanded attribute
  accordionItems.forEach((item) => {
    item.addEventListener('click', function () {
      // Toggle the aria-expanded attribute
      const isExpanded = this.getAttribute('aria-expanded') === 'true';

      // Set to opposite value
      this.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    });
  });
}

document.addEventListener('DOMContentLoaded', function () {
  // Setup checkout button handlers
  handleCheckout();

  // Initialize accordion for elements with the class 'accordion'
  initializeAccordion('.accordion button');
});
